import { motion } from 'framer-motion'
import ScrollReveal from '../components/shared/ScrollReveal'
import GradientText from '../components/shared/GradientText'
import GlowButton from '../components/shared/GlowButton'
import './DraftPages.css'

const Icons = {
  fileText: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  alertCircle: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  mail: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  )
}

export default function Terms() {
  const lastUpdated = 'January 28, 2025'

  return (
    <div className="draft-page privacy-page">
      {/* Hero */}
      <section className="draft-hero">
        <div className="container">
          <ScrollReveal>
            <span className="draft-badge">Legal</span>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h1 className="draft-title">
              Terms of{' '}
              <GradientText gradient="energy">Service</GradientText>
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="draft-subtitle">
              These Terms of Service govern your use of the AI4Solar platform and services
              provided by AI4Renewables. By using our services, you agree to these terms.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.3}>
            <p style={{ color: 'var(--color-gray-400)', fontSize: '0.875rem', marginTop: 'var(--space-md)' }}>
              Last updated: {lastUpdated}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Acceptance */}
      <section className="about-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Acceptance of Terms</h2>
            <p>Agreement to be bound by these terms</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <p>
                By accessing or using the AI4Solar platform, website, mobile applications, or any other
                services provided by AI4Renewables (collectively, the "Services"), you agree to be bound
                by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not
                access or use the Services.
              </p>
              <p style={{ marginTop: 'var(--space-lg)' }}>
                These Terms apply to all users of the Services, including users who are also contributors
                of content, information, and other materials or services. We reserve the right to modify
                these Terms at any time, and such modifications will be effective immediately upon posting.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Description of Services */}
      <section className="about-section about-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Description of Services</h2>
            <p>What AI4Solar provides</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <div className="legal-section">
                <h3>Platform Services</h3>
                <p>
                  AI4Solar is a solar monitoring and predictive maintenance platform that provides
                  real-time monitoring, analytics, alerts, and machine learning-powered insights for
                  solar energy installations. Our Services include but are not limited to:
                </p>
                <ul style={{ marginTop: 'var(--space-md)', marginLeft: 'var(--space-lg)' }}>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    Real-time monitoring of solar plant performance
                  </li>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    Predictive maintenance alerts and recommendations
                  </li>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    Energy production analytics and reporting
                  </li>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    API access for integration with third-party systems
                  </li>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    Mobile applications for iOS and Android
                  </li>
                </ul>
              </div>

              <div className="legal-section">
                <h3>Service Availability</h3>
                <p>
                  We strive to maintain high availability of our Services, but we do not guarantee
                  uninterrupted access. We may modify, suspend, or discontinue any aspect of the
                  Services at any time, with or without notice.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* User Accounts */}
      <section className="about-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>User Accounts</h2>
            <p>Account registration and responsibilities</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <div className="legal-section">
                <h3>Account Registration</h3>
                <p>
                  To access certain features of the Services, you must register for an account.
                  You agree to provide accurate, current, and complete information during registration
                  and to update such information to keep it accurate, current, and complete.
                </p>
              </div>

              <div className="legal-section">
                <h3>Account Security</h3>
                <p>
                  You are responsible for safeguarding your account credentials and for all activities
                  that occur under your account. You agree to notify us immediately of any unauthorized
                  use of your account or any other breach of security.
                </p>
              </div>

              <div className="legal-section">
                <h3>Account Termination</h3>
                <p>
                  We reserve the right to suspend or terminate your account at any time for any reason,
                  including if we reasonably believe you have violated these Terms. You may also
                  terminate your account at any time by contacting us.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Subscription and Payment */}
      <section className="about-section about-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Subscription and Payment</h2>
            <p>Billing terms and conditions</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <div className="legal-section">
                <h3>Subscription Plans</h3>
                <p>
                  Access to certain features of the Services requires a paid subscription. Subscription
                  plans and pricing are available on our website and may be modified at any time.
                  Changes to pricing will not affect existing subscriptions until renewal.
                </p>
              </div>

              <div className="legal-section">
                <h3>Payment Terms</h3>
                <p>
                  You agree to pay all fees associated with your subscription plan. Fees are billed
                  in advance on a monthly or annual basis, depending on your selected plan. All fees
                  are non-refundable except as expressly provided in these Terms.
                </p>
              </div>

              <div className="legal-section">
                <h3>Automatic Renewal</h3>
                <p>
                  Subscriptions automatically renew at the end of each billing period unless you
                  cancel before the renewal date. You may cancel your subscription at any time
                  through your account settings or by contacting our support team.
                </p>
              </div>

              <div className="legal-section">
                <h3>Taxes</h3>
                <p>
                  All fees are exclusive of taxes unless otherwise stated. You are responsible for
                  paying any applicable taxes, including VAT, based on your location.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Acceptable Use */}
      <section className="about-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Acceptable Use</h2>
            <p>Guidelines for using our Services</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <div className="legal-section">
                <h3>Permitted Use</h3>
                <p>
                  You may use the Services only for lawful purposes and in accordance with these Terms.
                  You agree to use the Services only for monitoring and managing solar energy installations
                  that you own or have authorization to manage.
                </p>
              </div>

              <div className="legal-section">
                <h3>Prohibited Activities</h3>
                <p>You agree not to:</p>
                <ul style={{ marginTop: 'var(--space-md)', marginLeft: 'var(--space-lg)' }}>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    Use the Services for any illegal purpose or in violation of any laws
                  </li>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    Attempt to gain unauthorized access to any portion of the Services
                  </li>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    Interfere with or disrupt the Services or servers connected to the Services
                  </li>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    Reverse engineer, decompile, or disassemble any portion of the Services
                  </li>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    Use any automated system to access the Services without our permission
                  </li>
                  <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                    Resell or redistribute the Services without our prior written consent
                  </li>
                </ul>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Intellectual Property */}
      <section className="about-section about-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Intellectual Property</h2>
            <p>Ownership and licensing rights</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <div className="legal-section">
                <h3>Our Intellectual Property</h3>
                <p>
                  The Services, including all content, features, and functionality, are owned by
                  AI4Renewables and are protected by international copyright, trademark, patent,
                  trade secret, and other intellectual property laws.
                </p>
              </div>

              <div className="legal-section">
                <h3>License to Use</h3>
                <p>
                  Subject to these Terms, we grant you a limited, non-exclusive, non-transferable,
                  revocable license to access and use the Services for your internal business purposes.
                  This license does not include the right to sublicense, modify, or create derivative
                  works of the Services.
                </p>
              </div>

              <div className="legal-section">
                <h3>Your Data</h3>
                <p>
                  You retain ownership of all data you submit to the Services ("Your Data"). By
                  submitting Your Data, you grant us a worldwide, royalty-free license to use,
                  process, and store Your Data solely for the purpose of providing the Services
                  to you and improving our platform.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Disclaimer and Limitations */}
      <section className="about-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Disclaimers and Limitations</h2>
            <p>Important legal limitations</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <div className="legal-section">
                <h3>Disclaimer of Warranties</h3>
                <p>
                  THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                  EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
                  MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
              </div>

              <div className="legal-section">
                <h3>Limitation of Liability</h3>
                <p>
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, AI4RENEWABLES SHALL NOT BE LIABLE FOR ANY
                  INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT
                  NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR IN
                  CONNECTION WITH THESE TERMS OR THE USE OF THE SERVICES.
                </p>
              </div>

              <div className="legal-section">
                <h3>Cap on Liability</h3>
                <p>
                  IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR
                  RELATING TO THESE TERMS OR THE SERVICES EXCEED THE AMOUNT PAID BY YOU TO US
                  DURING THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
                </p>
              </div>

              <div className="legal-highlight">
                <strong>Important:</strong> The predictions and recommendations provided by AI4Solar
                are based on machine learning models and should not be relied upon as the sole basis
                for operational decisions. Always verify critical information independently.
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Indemnification */}
      <section className="about-section about-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Indemnification</h2>
            <p>Your responsibility to defend us</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <p>
                You agree to indemnify, defend, and hold harmless AI4Renewables and its officers,
                directors, employees, agents, and affiliates from and against any and all claims,
                damages, losses, costs, and expenses (including reasonable attorneys' fees) arising
                out of or relating to:
              </p>
              <ul style={{ marginTop: 'var(--space-lg)', marginLeft: 'var(--space-lg)' }}>
                <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                  Your use of the Services
                </li>
                <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                  Your violation of these Terms
                </li>
                <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                  Your violation of any rights of another party
                </li>
                <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                  Your Data or any content you submit to the Services
                </li>
              </ul>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Governing Law */}
      <section className="about-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Governing Law and Disputes</h2>
            <p>Legal jurisdiction and dispute resolution</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <div className="legal-section">
                <h3>Governing Law</h3>
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of
                  the European Union and the member state where AI4Renewables is incorporated,
                  without regard to its conflict of law provisions.
                </p>
              </div>

              <div className="legal-section">
                <h3>Dispute Resolution</h3>
                <p>
                  Any dispute arising out of or relating to these Terms or the Services shall
                  first be attempted to be resolved through good-faith negotiations. If negotiations
                  fail, the dispute shall be submitted to binding arbitration in accordance with
                  the rules of the International Chamber of Commerce.
                </p>
              </div>

              <div className="legal-section">
                <h3>Class Action Waiver</h3>
                <p>
                  You agree that any dispute resolution proceedings will be conducted only on an
                  individual basis and not in a class, consolidated, or representative action.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* General Provisions */}
      <section className="about-section about-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>General Provisions</h2>
            <p>Additional legal terms</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <div className="legal-section">
                <h3>Entire Agreement</h3>
                <p>
                  These Terms, together with our Privacy Policy and any other agreements expressly
                  incorporated by reference, constitute the entire agreement between you and
                  AI4Renewables regarding the Services.
                </p>
              </div>

              <div className="legal-section">
                <h3>Severability</h3>
                <p>
                  If any provision of these Terms is found to be unenforceable or invalid, that
                  provision shall be limited or eliminated to the minimum extent necessary, and
                  the remaining provisions shall remain in full force and effect.
                </p>
              </div>

              <div className="legal-section">
                <h3>Waiver</h3>
                <p>
                  Our failure to enforce any right or provision of these Terms shall not be deemed
                  a waiver of such right or provision.
                </p>
              </div>

              <div className="legal-section">
                <h3>Assignment</h3>
                <p>
                  You may not assign or transfer these Terms or your rights under these Terms without
                  our prior written consent. We may assign our rights and obligations under these
                  Terms without restriction.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact */}
      <section className="draft-cta">
        <div className="container">
          <ScrollReveal className="draft-cta-content">
            <h2>Questions About These Terms?</h2>
            <p>
              If you have any questions about these Terms of Service,
              please contact our legal team.
            </p>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--space-md)',
              marginBottom: 'var(--space-xl)'
            }}>
              <a
                href="mailto:legal@ai4solar.com"
                style={{
                  color: 'var(--color-energy)',
                  fontSize: '1.125rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-sm)'
                }}
              >
                <span style={{ width: '20px', height: '20px' }}>{Icons.mail}</span>
                legal@ai4solar.com
              </a>
            </div>
            <div className="cta-buttons">
              <GlowButton href="/contact" variant="solar" size="large">
                Contact Us
              </GlowButton>
              <GlowButton href="/privacy" variant="outline" size="large">
                Privacy Policy
              </GlowButton>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}
